import { Button, Heading, Timeline, Typography } from '@altify/components';
import { daysOfWeek, useAppLanguage, useContent } from '@altify/utils';
import { useNavigate } from '@tanstack/react-router';
import { formatCurrency } from '@utils/currencies';
import {
  formatDateStringShortMonth,
  getNextDateFromDayOfMonth,
  getNextDateFromWeekday,
} from '@utils/date';
import { useProduct } from 'hooks/products/useProducts';
import { useRef } from 'react';
import { Animated, Dimensions, View } from 'react-native';
import ErrorImage from '../../../assets/Error.svg';
import { RecurringBuySchedule } from '../../models/RecurringBuySchedule';
import { recurringBuySharedContent } from '../../recurringBuyShared.content';
import { content } from './RecurringBuySubmitModal.content';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import { submitOrderContent } from 'app/_authenticated/trade/components/SubmitOrderModal/SubmitOrderModal.content';
import { tradeSharedContent } from 'app/_authenticated/trade/tradeShared.content';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import * as loaderAnimationData from '../../../assets/lottie/altify-loader-blue.json';
import * as successAnimationData from '../../../assets/lottie/altify-loader-success-white.json';

export const RecurringBuySubmitModal = ({
  schedule,
  fromAmount,
  toAmount,
  fromProductCode,
  toProductCode,
  tradeComplete,
  tradeError,
  onClose,
  onCancelTrade,
  onRetryTrade,
}: {
  schedule: RecurringBuySchedule;
  fromProductCode: string;
  toProductCode: string;
  fromAmount: number;
  toAmount?: number;
  tradeComplete?: boolean;
  tradeError?: boolean;
  onCancelTrade: () => void;
  onRetryTrade: () => void;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { product: toProduct } = useProduct(toProductCode);
  const animation = useRef(new Animated.Value(0)).current;
  const localizedContent = useContent(
    content,
    submitOrderContent,
    recurringBuySharedContent,
    tradeSharedContent,
  );
  const language = useAppLanguage();

  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;

  const animatedStyle = {
    backgroundColor: animation.interpolate({
      inputRange: [0, 1],
      outputRange: ['transparent', '#626AFF'],
    }),
    width: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, screenWidth * 2],
    }),
    height: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, screenHeight * 2],
    }),
    borderRadius: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, screenWidth],
    }),
  };

  const createdStep = {
    title: localizedContent.recurringCreated,
    subtitle: localizedContent.recurringSchedule(schedule),
    state: 'complete',
  } as const;

  const firstBuyStep = {
    title: `${localizedContent.firstBuyOf} ${formatCurrency(
      fromAmount,
      fromProductCode,
    )}`,
    subtitle: localizedContent.today,
    state: 'complete',
  } as const;

  const nextBuyStep = {
    title: `${localizedContent.nextBuyOf} ${formatCurrency(
      fromAmount,
      fromProductCode,
    )}`,
    subtitle:
      schedule?.interval === 'monthly'
        ? formatDateStringShortMonth(
            getNextDateFromDayOfMonth(schedule?.dayOfMonth),
            language,
          )
        : formatDateStringShortMonth(
            getNextDateFromWeekday(daysOfWeek.indexOf(schedule?.dayOfWeek)),
            language,
          ),
    state: 'pending',
  } as const;

  const timelineSteps = schedule.buyNow
    ? [createdStep, firstBuyStep, nextBuyStep]
    : [createdStep, nextBuyStep];

  const statusText = tradeComplete
    ? !schedule?.buyNow
      ? localizedContent.recurringBuyCreated
      : localizedContent.tradeComplete
    : !schedule?.buyNow
      ? localizedContent.creatingRecurringBuy
      : localizedContent.executingTrade;

  const statusSubtitleText = tradeComplete
    ? schedule?.buyNow
      ? localizedContent.bought
      : localizedContent.youWillBeBuying
    : schedule?.buyNow
      ? localizedContent.buying
      : localizedContent.youWillBeBuying;

  return (
    <DialogOrBottomSheet
      dismissable={tradeComplete}
      onClose={() => {
        navigate({ to: '/recurring-buys' });
        onClose?.();
      }}
      blurEnabled={true}
      footerContent={
        <>
          {tradeError ? (
            <View className="w-full items-center px-4 pb-2">
              <Button
                size="large"
                color="primary"
                onPress={() => {
                  onRetryTrade?.();
                }}
                text={localizedContent.retry}
                className="my-2 w-full"
              />
              <Button
                size="large"
                color="secondary"
                onPress={() => {
                  onCancelTrade?.();
                }}
                text={localizedContent.cancelTrade}
                className="mt-1 w-full"
              />
            </View>
          ) : (
            tradeComplete && (
              <View className="w-full items-center px-4 pb-4">
                <Button
                  size="large"
                  color="white"
                  onPress={() => {
                    navigate({ to: '/products' });
                    onClose?.();
                  }}
                  text={localizedContent.exploreProducts}
                  className="mt-2 w-full"
                />
                <Button
                  color="secondary"
                  text={localizedContent.manageRecurringBuys}
                  onPress={() => {
                    navigate({ to: '/recurring-buys' });
                    onClose?.();
                  }}
                  className="mt-3 w-full"
                />
              </View>
            )
          )}
        </>
      }
      backgroundColor={tradeComplete ? '#626AFF' : '#0C1C2C'}>
      <View className="w-full flex flex-col items-center px-4 py-4">
        <View className="w-full items-center justify-center">
          {tradeError ? (
            <View className="mb-4 mt-6 items-center">
              <img src={ErrorImage} />
              <Heading level={5} className="mt-4 text-center">
                {localizedContent.genericTradeError}
              </Heading>
            </View>
          ) : (
            <>
              <Animated.View
                style={[
                  { position: 'absolute', height: '100%' },
                  animatedStyle,
                ]}
              />
              <DotLottieReact
                data={
                  tradeComplete ? successAnimationData : loaderAnimationData
                }
                autoplay={true}
                loop={!tradeComplete}
                style={{ width: '100%', height: 100 }}
                key={tradeComplete ? 'success' : 'loader'}
              />
              <Heading level={4} className="mb-2">
                {statusText}
              </Heading>
              {!tradeComplete && (
                <Typography size="large" className="text-neutral-300">
                  {localizedContent.thisMayTakeAMoment}
                </Typography>
              )}
              <View className="mb-5" />
              <View>
                <CurrencyIcon
                  invertedColors={tradeComplete}
                  code={toProductCode}
                  bottomRightIcon="RECURRING_BUY"
                  cornerIconBackground={tradeComplete ? '#626AFF' : '#0C1C2C'}
                  size={40}
                />
              </View>
              <Typography size="large" className="mt-4">
                {statusSubtitleText}
              </Typography>
              <Heading level={2}>
                {!tradeComplete || !schedule?.buyNow
                  ? toProduct?.name
                  : formatCurrency(toAmount, toProductCode)}
              </Heading>
              {tradeComplete && (
                <Timeline className="my-4" steps={timelineSteps} />
              )}
            </>
          )}
        </View>
      </View>
    </DialogOrBottomSheet>
  );
};
